import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { CourseTranslation } from '@/components/courses';
import { useCourse } from '@/hooks/query';
import { FlexContainer } from '@/ui/styled-ui';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TranslationContainer = styled.div`
  background: var(--beige-main);
  border-radius: var(--semi-medium-border-radius);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 78px;

  &::-webkit-scrollbar-track {
    background-color: var(--beige-main);
  }
  &::-webkit-scrollbar-thumb {
    border: 4px solid var(--beige-main);
  }
  &::-webkit-scrollbar-corner {
    background-color: var(--beige-main);
  }
`;

export const CourseTranslatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const { course, isLoading } = useCourse({
    courseId,
  });

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  // Loading state while fetching course
  if (isLoading || !course) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
        <h3>{t('content.loading')}</h3>
      </FlexContainer>
    );
  }

  return (
    <PageContainer>
      <CoursesActionHeader course={course} />
      {course && <CoursesTabs course={course} />}

      <TranslationContainer>
        <CourseTranslation
          course={course}
          onCancel={() =>
            navigate(
              RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](course.id),
            )
          }
          onTranslationFinished={() => {
            // Delay navigation to allow banner to render
            // SessionStorage flag is set in CourseTranslation.tsx when translation finishes
            navigationTimeoutRef.current = setTimeout(() => {
              navigate(
                RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](course.id),
              );
            }, 500);
          }}
          onComplete={() =>
            navigate(
              RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](course.id),
            )
          }
          showSourceLanguageSelector={true}
        />
      </TranslationContainer>
    </PageContainer>
  );
};
